import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import {
  DevHubBox,
  DevHubFormControl,
  DevHubFormControlLabel,
  DevHubFormLabel,
  DevHubGrid,
  DevHubRadio,
  DevHubRadioGroup,
  DevHubTextField,
} from '@devxdiscover/devhub-ui';
import { PartySearch } from '../common/PartySearch';
import { ResponsibleParty } from '../insights/types/RoleTypes';
import { AppDetails, CorporateEntity } from './types/onboardingTypes';
import { ConfigFeatureCheck } from '../../util/ConfigFeatureCheck';
import { ConfigHelper, Feature } from '@devxdiscover/devhub-common';

interface Props {
  formData: AppDetails;
  setFormData: Dispatch<SetStateAction<AppDetails>>;
  setValidationHasError: Dispatch<SetStateAction<boolean>>;
}

/**
 * Form to gather basic application input for new app creation
 * @param props - The properties passed to the component.
 * @public
 */
export const AppCreationDetails = (props: Props) => {
  const { formData, setFormData } = props;
  const [isTouched, setIsTouched] = useState<boolean>(false);
  const [selectedParty, setSelectedParty] = useState<ResponsibleParty>(
    {} as ResponsibleParty,
  );
  const [urlError, setUrlError] = useState<boolean>(false);
  const isPrivacySurveyEnabled = ConfigHelper.instance.isFeatureEnabled(
    Feature.PRIVACY_SURVEY,
  );

  const nameValidation = (value: string) => {
    const regex = /[!@#$%^*(),?"':{}`|<>]/g;
    return !!(value?.length < 1 || value?.length > 75 || value?.match(regex));
  };

  const descriptionValidation = (value: string) => {
    const regex = /[!@#$%^*(),?"':{}`|<>]/g;
    return !!(
      value?.length > 1000 ||
      (value?.length < 20 && value?.length >= 0) ||
      value?.match(regex)
    );
  };

  const urlValidation = (value: string | undefined) => {
    if (!value || value?.length === 0) {
      return true;
    }
    try {
      const urlTest = new URL(value);
      return urlTest.protocol === 'http:' || urlTest.protocol === 'https:';
    } catch (_) {
      return false;
    }
  };

  const validateAppCreationDetails = (value: AppDetails) => {
    let expectedObjectKeys = [
      'entity',
      'consumerType',
      'status',
      'owner',
      'serviceLevelTier',
      'systemType',
      'name',
      'description',
    ];

    if (!isPrivacySurveyEnabled || value.entity !== CorporateEntity.CABLE) {
      expectedObjectKeys = expectedObjectKeys.filter(
        item => item !== 'consumerType' && item !== 'systemType',
      );
    }

    let hasError = false;

    if (Object.keys(value).length === 0) {
      hasError = true;
    }

    expectedObjectKeys.forEach(item => {
      if (!value.hasOwnProperty(item)) {
        hasError = true;
      }
    });

    if (!value?.owner?.login) {
      hasError = true;
    }

    if (
      nameValidation(formData.name) ||
      descriptionValidation(formData.description)
    ) {
      hasError = true;
    }

    if (value?.url && !urlValidation(value?.url)) {
      setUrlError(true);
      hasError = true;
    } else {
      setUrlError(false);
    }

    return hasError;
  };

  useEffect(() => {
    const prevDetails: AppDetails = formData;
    if (selectedParty?.login) {
      if (selectedParty?.login === 'clear') {
        prevDetails.owner = {} as ResponsibleParty;
      } else {
        prevDetails.owner = selectedParty;
      }
    }

    setFormData(prevDetails);
    props.setValidationHasError(validateAppCreationDetails(formData));
  }, [selectedParty]); // eslint-disable-line react-hooks/exhaustive-deps

  const onFormChange = (event: ChangeEvent<HTMLInputElement>) => {
    const textDevHubBoxName = event?.target?.name;
    const newValue = event?.target?.value.trim();
    const prevDetails: AppDetails = formData;

    prevDetails.serviceLevelTier = 4;
    prevDetails[textDevHubBoxName] = newValue;

    setFormData(prevDetails);
    props.setValidationHasError(validateAppCreationDetails(prevDetails));
  };

  return (
    <>
      <DevHubGrid container>
        <DevHubGrid container>
          <DevHubGrid item md={12} lg={6}>
            <DevHubTextField
              size="medium"
              variant="outlined"
              name="name"
              defaultValue={formData?.name}
              onChange={onFormChange}
              error={nameValidation(formData.name)}
              required
              label="Business Application Name"
              helperText={
                nameValidation(formData.name)
                  ? 'Name must be less than 75 characters, only special characters that are allowed: . & - '
                  : 'Short descriptive name of the application'
              }
            />
          </DevHubGrid>
          <DevHubGrid item md={12} lg={6}>
            <DevHubTextField
              variant="outlined"
              size="medium"
              multiline
              minRows={5}
              maxRows={5}
              required
              name="description"
              defaultValue={formData?.description}
              error={isTouched && descriptionValidation(formData?.description)}
              onChange={onFormChange}
              onBlur={() => setIsTouched(true)}
              label="Description"
              helperText={
                descriptionValidation(formData?.description)
                  ? 'Description must be between 20-1000 characters, and not contain special characters.'
                  : 'In business terms, what is the purpose and/or business value derived from the application.'
              }
            />
          </DevHubGrid>
        </DevHubGrid>

        <DevHubGrid container>
          <DevHubGrid item md={12} lg={6}>
            <PartySearch
              entity={props?.formData?.entity as CorporateEntity}
              name="ownerLogin"
              helperText="Primary contact for the application"
              label="Owner"
              groupEnabled={false}
              contractors={false}
              setSelectedParty={setSelectedParty}
              selectedParty={formData?.owner}
            />
          </DevHubGrid>
          <DevHubGrid item md={12} lg={6}>
            <DevHubTextField
              variant="outlined"
              name="url"
              error={urlError}
              defaultValue={formData?.url}
              onChange={onFormChange}
              helperText={
                urlError
                  ? 'Must be a valid url.'
                  : 'Main URL of the application'
              }
              label="URL"
            />
          </DevHubGrid>
        </DevHubGrid>
      </DevHubGrid>

      <DevHubBox>
        <DevHubGrid container spacing={1} margin="medium">
          <DevHubGrid item xs={12} lg={4}>
            <DevHubFormControl>
              <DevHubFormLabel>Status *</DevHubFormLabel>
              <DevHubRadioGroup
                name="status"
                onChange={onFormChange}
                defaultValue={formData?.status ?? ''}
              >
                <DevHubFormControlLabel
                  value="Active"
                  control={<DevHubRadio />}
                  label="Active"
                />
                <DevHubFormControlLabel
                  value="Pre-Prod"
                  control={<DevHubRadio />}
                  label="Pre-production"
                />
              </DevHubRadioGroup>
            </DevHubFormControl>
          </DevHubGrid>

          <ConfigFeatureCheck feature={Feature.PRIVACY_SURVEY}>
            {props.formData.entity === CorporateEntity.CABLE && (
              <>
                <DevHubGrid item xs={12} lg={4}>
                  <DevHubFormControl>
                    <DevHubFormLabel>System Type *</DevHubFormLabel>
                    <DevHubRadioGroup
                      name="systemType"
                      onChange={onFormChange}
                      defaultValue={formData?.systemType ?? ''}
                    >
                      <DevHubFormControlLabel
                        value="DataHub"
                        control={<DevHubRadio />}
                        label="Data Hub"
                      />
                      <DevHubFormControlLabel
                        value="DataSource"
                        control={<DevHubRadio />}
                        label="Source System"
                      />
                      <DevHubFormControlLabel
                        value="DataConsumer"
                        control={<DevHubRadio />}
                        label="Consuming System"
                      />
                    </DevHubRadioGroup>
                  </DevHubFormControl>
                </DevHubGrid>

                <DevHubGrid item xs={12} lg={4}>
                  <DevHubFormControl>
                    <DevHubFormLabel>Consumer Type *</DevHubFormLabel>
                    <DevHubRadioGroup
                      name="consumerType"
                      onChange={onFormChange}
                      defaultValue={formData?.consumerType ?? ''}
                    >
                      <DevHubFormControlLabel
                        value="Residential"
                        control={<DevHubRadio />}
                        label="Residential"
                      />
                      <DevHubFormControlLabel
                        value="Workforce"
                        control={<DevHubRadio />}
                        label="Workforce"
                      />
                      <DevHubFormControlLabel
                        value="ComcastBusiness"
                        control={<DevHubRadio />}
                        label="Comcast Business"
                      />
                      <DevHubFormControlLabel
                        value="ComcastTechnologySolutions"
                        control={<DevHubRadio />}
                        label="Comcast Technology Solutions"
                      />
                    </DevHubRadioGroup>
                  </DevHubFormControl>
                </DevHubGrid>
              </>
            )}
          </ConfigFeatureCheck>
        </DevHubGrid>
      </DevHubBox>
    </>
  );
};
